import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Database, Search, Trash2, Power, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const PAGE_SIZE = 25;

export default function DataManager() {
  const { authAxios } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("platform");
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collection, setCollection] = useState("");
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [qActive, setQActive] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // { kind: 'delete'|'toggle', is_active?: bool }
  const [reason, setReason] = useState("");
  const [acting, setActing] = useState(false);

  const currentCol = collections.find(c => c.name === collection);
  const isCritical = !!currentCol?.is_critical;
  const supportsSoftToggle = !!currentCol?.supports_soft_toggle;

  useEffect(() => {
    authAxios.get("/platform/tenants").then(r => setTenants(r.data || []))
      .catch(err => console.error("Tenants load failed:", err?.message || err));
  }, []);

  const loadCollections = async () => {
    if (!tenantId) return;
    setCollectionsLoading(true);
    setCollection(""); setRows([]); setTotalRows(0); setSelected(new Set());
    try {
      const res = await authAxios.get(`/platform/data-manager/tenants/${tenantId}/collections`);
      setCollections(res.data.collections || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load collections"); }
    finally { setCollectionsLoading(false); }
  };

  useEffect(() => { loadCollections(); /* eslint-disable-next-line */ }, [tenantId]);

  const loadRows = async (opts = {}) => {
    if (!tenantId || !collection) return;
    setRowsLoading(true);
    const nextSkip = opts.skip !== undefined ? opts.skip : skip;
    const nextQ = opts.q !== undefined ? opts.q : qActive;
    try {
      const res = await authAxios.get(
        `/platform/data-manager/tenants/${tenantId}/collections/${collection}`,
        { params: { q: nextQ || "", limit: PAGE_SIZE, skip: nextSkip } }
      );
      setRows(res.data.rows || []);
      setTotalRows(res.data.total || 0);
      setSkip(nextSkip);
      setSelected(new Set());
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load rows"); }
    finally { setRowsLoading(false); }
  };

  useEffect(() => {
    if (collection) loadRows({ skip: 0, q: "" });
    // eslint-disable-next-line
  }, [collection]);

  const applySearch = () => { setQActive(q); loadRows({ skip: 0, q }); };

  const toggleRow = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };
  const toggleAllOnPage = () => {
    const pageKeys = rows.map(r => r._row_key).filter(Boolean);
    const allSelected = pageKeys.every(k => selected.has(k));
    const next = new Set(selected);
    if (allSelected) pageKeys.forEach(k => next.delete(k));
    else pageKeys.forEach(k => next.add(k));
    setSelected(next);
  };

  const openConfirm = (kind, is_active) => {
    if (selected.size === 0) return toast.error("Select at least one row");
    setConfirm({ kind, is_active });
    setReason("");
  };

  const executeAction = async () => {
    if (!confirm) return;
    if (!reason || reason.trim().length < 12) return toast.error("Reason must be at least 12 characters");
    setActing(true);
    const ids = Array.from(selected);
    try {
      if (confirm.kind === "delete") {
        const res = await authAxios.post(
          `/platform/data-manager/tenants/${tenantId}/collections/${collection}/delete`,
          { ids, reason: reason.trim() }
        );
        toast.success(`Deleted ${res.data.deleted_count} of ${res.data.requested} row(s)`);
      } else {
        const res = await authAxios.post(
          `/platform/data-manager/tenants/${tenantId}/collections/${collection}/toggle-active`,
          { ids, is_active: !!confirm.is_active, reason: reason.trim() }
        );
        toast.success(`Marked ${res.data.modified_count} row(s) is_active=${confirm.is_active}`);
      }
      setConfirm(null); setReason(""); setSelected(new Set());
      loadCollections();
      loadRows({ skip });
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setActing(false); }
  };

  const renderCell = (row, field) => {
    const v = row[field];
    if (v === null || v === undefined) return <span className="text-slate-300">—</span>;
    if (typeof v === "boolean") return v ? <span className="text-green-700 font-bold">true</span> : <span className="text-red-600 font-bold">false</span>;
    if (typeof v === "object") return <span className="font-mono text-[10px]">{JSON.stringify(v).slice(0, 40)}</span>;
    return <span className="truncate max-w-[16ch] inline-block align-bottom">{String(v)}</span>;
  };

  const rowFields = rows[0]
    ? Object.keys(rows[0]).filter(k => k !== "_row_key")
    : [];

  const pageStart = totalRows === 0 ? 0 : skip + 1;
  const pageEnd = Math.min(skip + rows.length, totalRows);

  return (
    <div data-testid="data-manager">
      <div className="flex items-center gap-2 mb-4">
        <Database className="w-5 h-5 text-red-600" />
        <h2 className="text-lg font-bold text-[#1e3a5f]">Data Manager</h2>
        <span className="ml-auto text-[11px] text-slate-500">Destructive. Every action is audit-logged.</span>
      </div>

      {/* Scope picker */}
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
        <label className="text-xs text-slate-500 mb-1 block">Scope</label>
        <select
          value={tenantId}
          onChange={e => setTenantId(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border text-sm"
          data-testid="dm-tenant-select"
        >
          <option value="platform">Platform (sys-level: sys_admins, tenants, module_pricing, gdpr_audit…)</option>
          {tenants.map(t => (
            <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
          ))}
        </select>
      </div>

      {/* Collection picker */}
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-sm text-[#1e3a5f]">Collections</h3>
          <button
            onClick={loadCollections}
            disabled={collectionsLoading}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
            data-testid="dm-refresh-collections"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${collectionsLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1" data-testid="dm-collections-grid">
          {collections.map(c => {
            const active = c.name === collection;
            const base = "text-left px-3 py-2 rounded-xl border text-xs font-medium transition flex items-center justify-between gap-2";
            const cls = c.is_critical
              ? `${base} ${active ? "bg-red-600 border-red-700 text-white" : "bg-red-50 border-red-200 text-red-800 hover:bg-red-100"}`
              : `${base} ${active ? "bg-[#1e3a5f] border-[#1e3a5f] text-white" : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"}`;
            return (
              <button
                key={c.name}
                onClick={() => setCollection(c.name)}
                className={cls}
                data-testid={`dm-col-${c.name}`}
                title={c.is_critical ? "Critical collection — extra care" : undefined}
              >
                <span className="truncate flex items-center gap-1.5">
                  {c.is_critical && <AlertTriangle className="w-3 h-3 shrink-0" />}
                  <span className="font-mono truncate">{c.name}</span>
                </span>
                <span className={`shrink-0 tabular-nums ${active ? "" : c.is_critical ? "text-red-600" : "text-slate-500"}`}>{c.count}</span>
              </button>
            );
          })}
          {collections.length === 0 && !collectionsLoading && (
            <p className="text-xs text-slate-400 col-span-full">No collections in this scope yet.</p>
          )}
        </div>
        {collections.some(c => c.is_critical) && (
          <p className="mt-2 text-[10px] text-slate-500 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 text-red-500" /> Red items are critical (auth / config / master data). Handle with care.
          </p>
        )}
      </div>

      {/* Row browser + actions */}
      {collection && (
        <div
          className={`bg-white rounded-2xl p-4 shadow-sm mb-4 ${isCritical ? "border-2 border-red-200" : ""}`}
          data-testid="dm-rows-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            {isCritical && <AlertTriangle className="w-4 h-4 text-red-600" />}
            <h3 className={`font-bold text-sm ${isCritical ? "text-red-700" : "text-[#1e3a5f]"}`}>
              {collection}
            </h3>
            <span className="text-[11px] text-slate-500">({totalRows} total)</span>
            {isCritical && (
              <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 px-2 py-0.5 rounded">
                Critical
              </span>
            )}
          </div>

          {/* Search */}
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                onKeyDown={e => e.key === "Enter" && applySearch()}
                placeholder="Search name / email / id / member_id …"
                className="w-full pl-8 pr-3 py-2 rounded-xl border text-sm"
                data-testid="dm-search-input"
              />
            </div>
            <button
              onClick={applySearch}
              className="px-3 py-2 rounded-xl bg-slate-700 text-white text-xs font-bold"
              data-testid="dm-search-btn"
            >
              Search
            </button>
            {qActive && (
              <button
                onClick={() => { setQ(""); setQActive(""); loadRows({ skip: 0, q: "" }); }}
                className="px-3 py-2 rounded-xl border text-xs text-slate-600"
                data-testid="dm-search-clear"
              >
                Clear
              </button>
            )}
          </div>

          {/* Action bar */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <button
              onClick={() => openConfirm("delete")}
              disabled={selected.size === 0}
              className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5"
              data-testid="dm-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete {selected.size > 0 && `(${selected.size})`}
            </button>
            {supportsSoftToggle && (
              <>
                <button
                  onClick={() => openConfirm("toggle", false)}
                  disabled={selected.size === 0}
                  className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5"
                  data-testid="dm-deactivate-btn"
                >
                  <Power className="w-3.5 h-3.5" /> Set inactive
                </button>
                <button
                  onClick={() => openConfirm("toggle", true)}
                  disabled={selected.size === 0}
                  className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5"
                  data-testid="dm-activate-btn"
                >
                  <Check className="w-3.5 h-3.5" /> Set active
                </button>
              </>
            )}
            <span className="ml-auto text-[11px] text-slate-500 tabular-nums">
              {rowsLoading ? "loading…" : `${pageStart}–${pageEnd} of ${totalRows}`}
            </span>
          </div>

          {/* Rows table */}
          <div className="overflow-x-auto border rounded-xl">
            <table className="w-full text-xs" data-testid="dm-rows-table">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                <tr>
                  <th className="p-2 w-8">
                    <input
                      type="checkbox"
                      onChange={toggleAllOnPage}
                      checked={rows.length > 0 && rows.every(r => selected.has(r._row_key))}
                      data-testid="dm-select-all"
                    />
                  </th>
                  {rowFields.map(f => (
                    <th key={f} className="p-2 text-left font-semibold tracking-wider">{f}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const key = r._row_key || `idx-${i}`;
                  const checked = selected.has(r._row_key);
                  return (
                    <tr
                      key={key}
                      className={`border-t ${checked ? (isCritical ? "bg-red-50" : "bg-amber-50") : "hover:bg-slate-50"}`}
                      data-testid={`dm-row-${key}`}
                    >
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRow(r._row_key)}
                          disabled={!r._row_key}
                          data-testid={`dm-row-check-${key}`}
                        />
                      </td>
                      {rowFields.map(f => (
                        <td key={f} className="p-2 font-mono text-[11px] text-slate-700">
                          {renderCell(r, f)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {rows.length === 0 && !rowsLoading && (
                  <tr><td colSpan={rowFields.length + 1} className="p-6 text-center text-slate-400 text-xs">No rows.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalRows > PAGE_SIZE && (
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                onClick={() => loadRows({ skip: Math.max(0, skip - PAGE_SIZE) })}
                disabled={skip === 0 || rowsLoading}
                className="p-1.5 rounded-lg border text-slate-600 disabled:opacity-40"
                data-testid="dm-prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => loadRows({ skip: skip + PAGE_SIZE })}
                disabled={skip + PAGE_SIZE >= totalRows || rowsLoading}
                className="p-1.5 rounded-lg border text-slate-600 disabled:opacity-40"
                data-testid="dm-next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Confirm dialog */}
      <AlertDialog open={!!confirm} onOpenChange={o => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="dm-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {confirm?.kind === "delete" ? <Trash2 className="w-5 h-5 text-red-600" /> : <Power className="w-5 h-5 text-amber-500" />}
              {confirm?.kind === "delete"
                ? `Delete ${selected.size} row(s) from ${collection}?`
                : `Mark ${selected.size} row(s) is_active=${confirm?.is_active}?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              {isCritical && (
                <span className="block mb-2 text-red-700 font-bold flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> This is a critical collection.
                </span>
              )}
              {confirm?.kind === "delete"
                ? "Hard deletion is permanent. Rows will be removed immediately and cannot be restored."
                : "Soft toggle only updates is_active. Reversible from this same panel."}
              <span className="block mt-2 text-slate-500">A reason (≥12 chars) will be recorded in the audit log along with the exact list of IDs.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-2">
            <textarea
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Reason (why is this necessary?)"
              className="w-full px-3 py-2 rounded-lg border text-sm"
              data-testid="dm-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dm-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={acting || reason.trim().length < 12}
              onClick={executeAction}
              className={confirm?.kind === "delete" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-amber-500 hover:bg-amber-600 text-white"}
              data-testid="dm-confirm-btn"
            >
              {acting ? "Working…" : confirm?.kind === "delete" ? "Delete permanently" : "Apply"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
